import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CalendarDays, Wallet, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { GF_DEF, semanasDelMes, aplicacion, diasSemanaEnMes } from "@/lib/gastoFijo";
import { weekLabel } from "@/lib/semanas";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const mx = (v) => `$${Math.round(v).toLocaleString("es-MX")}`;
const fechaTxt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
const wKey = (w) => `${w.year}-${w.week}`;

function Campo({ k, label, value, onChange }) {
  return (
    <label className="flex items-center justify-between gap-4 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors">
      <span className="text-sm text-slate-700">{label}</span>
      <span className="relative w-48">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
        <input type="number" min={0} step="0.01" value={value ?? ""} onChange={(e) => onChange(k, e.target.value)}
          onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
          className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-7 pr-3 text-sm text-right tabular-nums text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
          data-testid={`gf-input-${k}`} />
      </span>
    </label>
  );
}

const Dato = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" />{label}</p>
    <p className="text-sm font-semibold text-slate-900 mt-0.5" data-testid={testid}>{value}</p>
  </div>
);

// Crea (edit = null) o edita el ejercicio actual; el mes/año vienen de la pantalla principal.
const fechaCorta = (y, m, d) => `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;

// Ejercicio inicial de Gestión Planta: cubre el mes completo, sin selector de semana.
function PeriodoInicial({ year, month, dias, mesBloqueado }) {
  return (
    <div className="space-y-2" data-testid="gf-periodo-inicial">
      <div className="grid grid-cols-3 gap-2">
        <Dato label="Número de ejercicio" value="1 · Inicial" testid="gf-dlg-numero" />
        <Dato label="Inicio de aplicación" value={fechaCorta(year, month, 1)} testid="gf-dlg-inicio" />
        <Dato label="Fin de aplicación" value={fechaCorta(year, month, dias)} testid="gf-dlg-fin" />
      </div>
      <p className="flex items-center gap-1 text-[11px] text-slate-500" data-testid="gf-semana-no-seleccionable"><Lock className="w-3 h-3" />Semana de aplicación no seleccionable: el primer ejercicio cubre el mes completo.</p>
      {mesBloqueado && <p className="flex items-center gap-1 text-[11px] text-red-600" data-testid="gf-mes-aviso"><Lock className="w-3 h-3" />No tienes permisos para registrar ejercicios en meses anteriores.</p>}
    </div>
  );
}

export function GastoFijoDialog({ open, onClose, planta, structure, ym, edit, base, inicial: esInicial, onSaved, def = GF_DEF }) {
  const CAPTURA = def.captura;
  const [wk, setWk] = useState("");
  const [fecha, setFecha] = useState("");
  const { data: cfg } = useQuery({ queryKey: [def.mod, "config"], queryFn: async () => (await api.get(`/control-semanal/${def.mod}/config`)).data });
  const fechaEditable = !!cfg?.fecha_editable;
  const { user } = useAuth();
  const actual = cfg?.semana_actual;
  const bloqueada = (w) => !user?.is_admin && (!actual || w.year * 100 + w.week < actual.year * 100 + actual.week);
  const [imp, setImp] = useState({});
  const [saving, setSaving] = useState(false);
  const year = edit?.year ?? ym.year;
  const month = edit?.month ?? ym.month;
  const semanas = semanasDelMes(year, month);
  useEffect(() => {
    if (open) { setWk(edit ? `${edit.week_year}-${edit.week}` : ""); setFecha(edit?.fecha_registro || cfg?.hoy || ""); setImp(edit ? { ...edit.importes } : Object.fromEntries(Object.entries(base?.importes || {}).map(([k, v]) => [k, v == null ? v : Math.round(v * 100) / 100]))); }
  }, [open, edit, base, cfg?.hoy]);
  const inicial = !!def.inicioMes && !!esInicial;
  const diasMes = new Date(year, month, 0).getDate();
  const mesBloqueado = inicial && !user?.is_admin && !!cfg?.hoy && year * 100 + month < Number(cfg.hoy.slice(0, 4)) * 100 + Number(cfg.hoy.slice(5, 7));
  const sel = inicial ? semanas[0] : semanas.find((w) => wKey(w) === wk && !bloqueada(w));
  const dias = inicial ? diasMes : sel ? aplicacion(year, month, sel.year, sel.week) : null;
  const set = (k, v) => setImp({ ...imp, [k]: v });
  const submit = async () => {
    if (!sel) { toast.error("Selecciona la semana de aplicación."); return; }
    setSaving(true);
    try {
      const importes = Object.fromEntries(CAPTURA.map(([k]) => [k, imp[k] === "" || imp[k] == null ? null : Number(imp[k])]));
      const extra = fechaEditable && fecha ? { fecha_registro: fecha } : {};
      const res = edit
        ? await api.put(`/control-semanal/${def.mod}/ejercicios/${edit.id}`, { structure, week_year: sel.year, week: sel.week, importes, ...extra })
        : await api.post(`/control-semanal/${def.mod}/ejercicios`, { planta, structure, year, month, week_year: sel.year, week: sel.week, importes, ...extra });
      toast.success(edit ? "Ejercicio actualizado." : "Ejercicio registrado.");
      onSaved(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-white text-slate-900 p-0 overflow-hidden gap-0" data-testid="gf-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
            <div className="text-left">
              <DialogTitle className="text-lg font-bold text-slate-900">{edit ? "Editar ejercicio actual" : `Nuevo ejercicio de ${def.titulo.toLowerCase()}`}</DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">{planta} · {def.bloque}</p>
            </div>
          </div>
        </DialogHeader>
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-[#2F75B5]">A partir de:</p>
            {inicial ? <PeriodoInicial year={year} month={month} dias={diasMes} mesBloqueado={mesBloqueado} /> : <label className="block">
              <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />Semana de aplicación</span>
              <Select value={wk} onValueChange={setWk}>
                <SelectTrigger className="mt-1.5 h-10 bg-white border-slate-200 text-sm text-slate-900" data-testid="gf-semana"><SelectValue placeholder="Selecciona una semana" /></SelectTrigger>
                <SelectContent>{semanas.map((w) => {
                  const n = diasSemanaEnMes(year, month, w.year, w.week);
                  const lock = bloqueada(w);
                  return (
                    <SelectItem key={wKey(w)} value={wKey(w)} disabled={lock} data-testid={`gf-semana-${w.week}`} data-bloqueada={lock}>
                      {lock && <Lock className="w-3 h-3 inline mr-1 -mt-0.5" />}{weekLabel(w.year, w.week)} <span className="ml-1 text-xs text-slate-500" data-testid={`gf-semana-dias-${w.week}`}>({n} {n === 1 ? "día" : "días"} en {MESES[month - 1]})</span>
                    </SelectItem>
                  );
                })}</SelectContent>
              </Select>
              {semanas.some(bloqueada) && <span className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500" data-testid="gf-semana-aviso"><Lock className="w-3 h-3" />No tienes permisos para registrar ejercicios en semanas anteriores.</span>}
            </label>}
            <p className="pt-1 text-xs font-bold uppercase tracking-wider text-[#2F75B5]">Información del ejercicio</p>
            <div className="grid grid-cols-3 gap-2">
              <Dato label="Mes de ejercicio" value={`${MESES[month - 1]} de ${year}`} testid="gf-dlg-mes" />
              {fechaEditable ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Fecha de registro · modo pruebas</p>
                  <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="gf-dlg-fecha-input"
                    className="mt-0.5 w-full bg-transparent text-sm font-semibold text-slate-900 focus:outline-none" />
                </div>
              ) : <Dato label="Fecha de registro" value={edit?.fecha_registro || cfg?.hoy ? fechaTxt(edit?.fecha_registro || cfg.hoy) : "—"} testid="gf-dlg-fecha" />}
              <Dato label="Días de aplicación" value={dias ?? "—"} testid="gf-dlg-dias" />
            </div>
          </div>
          {def.grupos.map(([titulo, campos]) => (
            <div key={titulo}>
              <p className="px-3 mb-1 text-xs font-bold uppercase tracking-wider text-[#2F75B5]">{titulo}</p>
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                {campos.map(([k, label]) => <Campo key={k} k={k} label={label} value={imp[k]} onChange={set} />)}
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">{def.totalLabel}: <b className="text-slate-900 tabular-nums" data-testid="gf-dialog-total">{mx(def.totales(Object.fromEntries(CAPTURA.map(([k]) => [k, Number(imp[k]) || 0]))).total)}</b></p>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-9 px-4 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-100" data-testid="gf-cancel">Cancelar</button>
            <button onClick={submit} disabled={saving || mesBloqueado} className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="gf-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}{edit ? "Guardar cambios" : "Registrar ejercicio"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
